import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Check, X, Search, ChevronDown, ChevronRight, Save, Shield } from 'lucide-react';
import { getAppSettings, updateAppSettings } from '../api';
import { CP_BASE } from '@/lib/http';
import { useAuthStore } from '@/store/authStore';

interface AllowlistEntry {
  agent_id: string;
  workflows?: string[];
}

interface AgentDef {
  id: string;
  name: string;
  description?: string;
  domain?: string;
  volcano_config?: { workflows?: Record<string, any> };
}

interface QuotaSettings {
  executions_per_minute?: number;
  executions_per_day?: number;
}

interface AgentAllowlistEditorProps {
  appId: string;
  allowlist: AllowlistEntry[];
  quotas?: QuotaSettings;
  onSave?: () => void;
}

export function AgentAllowlistEditor({ appId, allowlist: initialAllowlist, quotas: initialQuotas, onSave }: AgentAllowlistEditorProps) {
  const [allAgents, setAllAgents] = useState<AgentDef[]>([]);
  const [allowlist, setAllowlist] = useState<AllowlistEntry[]>(initialAllowlist || []);
  const [quotas, setQuotas] = useState<QuotaSettings>(initialQuotas || { executions_per_minute: 30, executions_per_day: 5000 });
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Fetch all agents from registry
  useEffect(() => {
    async function load() {
      try {
        const s = useAuthStore.getState();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (s?.token) headers['Authorization'] = `Bearer ${s.token}`;
        if (s?.selectedTenant) headers['x-tenant-id'] = s.selectedTenant;

        const resp = await fetch(`${CP_BASE}/api/agents?limit=100`, { headers });
        const data = await resp.json();
        const agents: AgentDef[] = data?.agents || data?.data || [];
        setAllAgents(agents);
      } catch {
        // ignore
      }
    }
    load();
  }, []);

  const allowedIds = new Set(allowlist.map(e => typeof e === 'string' ? e : e.agent_id));

  function toggleAgent(agentId: string) {
    if (allowedIds.has(agentId)) {
      setAllowlist(allowlist.filter(e => (typeof e === 'string' ? e : e.agent_id) !== agentId));
    } else {
      setAllowlist([...allowlist, { agent_id: agentId }]);
    }
    setDirty(true);
  }

  function toggleWorkflow(agentId: string, workflow: string) {
    const entry = allowlist.find(e => (typeof e === 'string' ? e : e.agent_id) === agentId);
    if (!entry || typeof entry === 'string') return;

    const wfs = Array.isArray(entry.workflows) ? [...entry.workflows] : [];
    const idx = wfs.indexOf(workflow);
    if (idx >= 0) {
      wfs.splice(idx, 1);
    } else {
      wfs.push(workflow);
    }

    setAllowlist(allowlist.map(e => {
      const id = typeof e === 'string' ? e : e.agent_id;
      if (id === agentId) return { agent_id: agentId, workflows: wfs.length > 0 ? wfs : undefined };
      return e;
    }));
    setDirty(true);
  }

  function toggleExpand(agentId: string) {
    const next = new Set(expanded);
    if (next.has(agentId)) next.delete(agentId);
    else next.add(agentId);
    setExpanded(next);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateAppSettings(appId, { agents: { allowlist, quotas } });
      setDirty(false);
      onSave?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Group agents by domain
  const domainGroups: Record<string, AgentDef[]> = {};
  for (const agent of allAgents) {
    const d = agent.domain || 'other';
    if (!domainGroups[d]) domainGroups[d] = [];
    domainGroups[d].push(agent);
  }

  const filtered = search
    ? allAgents.filter(a => a.id.includes(search.toLowerCase()) || (a.name || '').toLowerCase().includes(search.toLowerCase()))
    : allAgents;

  const filteredGroups: Record<string, AgentDef[]> = {};
  for (const agent of filtered) {
    const d = agent.domain || 'other';
    if (!filteredGroups[d]) filteredGroups[d] = [];
    filteredGroups[d].push(agent);
  }

  const domainColors: Record<string, string> = {
    pim: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    dam: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    iot: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    relationships: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    contextualizer: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    system: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">
            <Shield className="h-4 w-4" /> Agent Allowlist
          </h3>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px]">{allowlist.length} allowed</Badge>
            {dirty && (
              <Button size="sm" className="text-xs h-7" onClick={handleSave} disabled={saving}>
                <Save className="h-3 w-3 mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="pl-8 text-xs h-8"
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="max-h-80 overflow-y-auto space-y-1 pr-1">
          {Object.entries(filteredGroups).sort().map(([domain, agents]) => (
            <div key={domain}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 mb-0.5 px-1">{domain}</p>
              {agents.map(agent => {
                const isAllowed = allowedIds.has(agent.id);
                const isExp = expanded.has(agent.id);
                const workflows = agent.volcano_config?.workflows ? Object.keys(agent.volcano_config.workflows) : [];
                const entry = allowlist.find(e => (typeof e === 'string' ? e : e.agent_id) === agent.id);
                const allowedWfs = typeof entry === 'object' && Array.isArray(entry?.workflows) ? entry.workflows : [];

                return (
                  <div key={agent.id} className={`rounded border px-2 py-1.5 text-xs ${isAllowed ? 'border-primary/30 bg-primary/5' : 'border-transparent hover:bg-muted/50'}`}>
                    <div className="flex items-center gap-2">
                      <button
                        className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${isAllowed ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'}`}
                        onClick={() => toggleAgent(agent.id)}
                      >
                        {isAllowed && <Check className="h-3 w-3" />}
                      </button>
                      <span className="font-medium truncate flex-1">{agent.name || agent.id}</span>
                      <Badge className={`text-[9px] ${domainColors[domain] || 'bg-gray-100 text-gray-800'}`}>{domain}</Badge>
                      {workflows.length > 0 && isAllowed && (
                        <button className="text-muted-foreground hover:text-foreground" onClick={() => toggleExpand(agent.id)}>
                          {isExp ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                    {agent.description && <p className="text-[10px] text-muted-foreground mt-0.5 ml-7 truncate">{agent.description}</p>}

                    {isExp && isAllowed && workflows.length > 0 && (
                      <div className="ml-7 mt-1.5 space-y-0.5">
                        <p className="text-[10px] text-muted-foreground">Workflows ({allowedWfs.length || 'all'} of {workflows.length}):</p>
                        {workflows.map(wf => {
                          const wfAllowed = allowedWfs.length === 0 || allowedWfs.includes(wf);
                          return (
                            <label key={wf} className="flex items-center gap-1.5 text-[10px] cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={wfAllowed}
                                onChange={() => toggleWorkflow(agent.id, wf)}
                                className="h-3 w-3 rounded"
                              />
                              <code>{wf}</code>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="border-t pt-3 mt-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Rate Limits</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground">Executions / minute</label>
              <Input
                type="number"
                className="text-xs h-7 mt-0.5"
                value={quotas.executions_per_minute ?? 30}
                onChange={(e) => { setQuotas({ ...quotas, executions_per_minute: parseInt(e.target.value) || 30 }); setDirty(true); }}
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">Executions / day</label>
              <Input
                type="number"
                className="text-xs h-7 mt-0.5"
                value={quotas.executions_per_day ?? 5000}
                onChange={(e) => { setQuotas({ ...quotas, executions_per_day: parseInt(e.target.value) || 5000 }); setDirty(true); }}
              />
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </CardContent>
    </Card>
  );
}
